/**
 * Dated teacher comments (v3.32.0).
 *
 * Student doc fields:
 *   teacherComments          — CURRENT comment text (string; pre-v3.32.0
 *                              surface, still what prints and prefills)
 *   teacherCommentsUpdatedAt — local 'YYYY-MM-DD' of the last save
 *   teacherCommentsHistory   — [{ text, date }] oldest-first; every saved
 *                              comment is preserved here so teachers can look
 *                              back at what they wrote earlier in the year.
 *
 * Same-day saves UPDATE today's history entry (blur fires on every edit —
 * without this, one afternoon of tweaking would spam the history); a save on
 * a new day APPENDS. Clearing the box the same day removes today's entry;
 * older entries are never touched.
 */

/** Local calendar date as 'YYYY-MM-DD' (never toISOString — that's UTC). */
export function localDateString(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' → 'July 14, 2026' (parsed as LOCAL date). '' for missing/bad input. */
export function formatCommentDate(dateStr) {
  if (typeof dateStr !== 'string') return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Compute the fields to save for a comment edit. Pure — no Firestore here.
 *
 * @param {Object} studentData  current doc data (teacherComments,
 *                              teacherCommentsUpdatedAt, teacherCommentsHistory)
 * @param {string} newText      the comment as it now reads in the box
 * @param {string} today        localDateString() (injectable for tests)
 * @returns {{ teacherComments, teacherCommentsUpdatedAt, teacherCommentsHistory }}
 */
export function buildCommentsSave(studentData, newText, today = localDateString()) {
  let history = Array.isArray(studentData?.teacherCommentsHistory)
    ? [...studentData.teacherCommentsHistory]
    : [];

  // Migrate a pre-v3.32.0 comment (string only, no history) into the history
  // so it isn't lost the first time the teacher writes something new.
  const legacy = (studentData?.teacherComments || '').trim();
  if (history.length === 0 && legacy) {
    history.push({ text: studentData.teacherComments, date: studentData?.teacherCommentsUpdatedAt || null });
  }

  const last = history[history.length - 1];
  if (newText.trim()) {
    if (last && last.date === today) {
      history = [...history.slice(0, -1), { text: newText, date: today }];
    } else {
      history = [...history, { text: newText, date: today }];
    }
  } else if (last && last.date === today) {
    // Cleared the same day it was written — drop today's entry, keep the rest.
    history = history.slice(0, -1);
  }

  return {
    teacherComments: newText,
    teacherCommentsUpdatedAt: today,
    teacherCommentsHistory: history,
  };
}

/**
 * History entries to show under "previous comments": everything except the
 * entry that currently fills the comment box (the latest), newest first.
 */
export function previousComments(history) {
  if (!Array.isArray(history) || history.length <= 1) return [];
  return history.slice(0, -1).reverse();
}
